// tabs/movement.ts — client-side port of the PC sheet's Movement tab
//   options/game/character_sheet_display/movement/_movement_inc.php (~906 lines)
//
// Held-blob presentation (no endpoint): speeds (computed.creature.speeds.*), status-gated stance/
// mount/vehicle actions, disabled collision recovery, and haven travel — all from the cached caster
// blob (incl. nested encounter_arr/campaign_arr). Every button delegates to the existing template.js globals
// (render-only port). Ports render_speed_btn; reuses
// renderSimpleTriggerAbilityButton for Evade.

import type { CachedCaster } from '../types';
import { refuseUnported } from '../../janus-engine/unported';

import { renderSimpleTriggerAbilityButton } from '../ability-button';
import { isPlayerCharacter } from '../read-helpers';
import { WEATHER_TYPE_LIST, effectiveEncounterLightId, lightTypeName } from '../../shared/env-types';
import { patchMarkup, rememberPatchMarkup } from '../patch-markup';
import { replaceHydratedHtml } from '../../shared/replace-hydrated-html';

type MovementWindow = Window & {
    updatePreferredSpeedType?: (speedTypeId: string, casterId?: string) => unknown;
    handleAbilityBtnSingleClick?: (abilityId: string) => unknown;
    ftDispatchAbilityClick?: (casterId: string, abilityId: string) => boolean;
    ftLegacyAbilityClickAs?: (casterId: string, abilityId: string) => void;
    ftGeometryRelocation?: (data: Record<string, unknown>) => Promise<Record<string, unknown>>;
    FT_STEALTH_V2_ENABLED?: boolean;
};

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function scalarOf(value: unknown): unknown {
    return Array.isArray(value) ? value[0] : value;
}

function movementAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-movement-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function movementAbilityAttrs(abilityId: string): string {
    return movementAttrs('ability-click', { abilityId });
}

function stealthV2Enabled(): boolean {
    return (window as MovementWindow).FT_STEALTH_V2_ENABLED !== false;
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-movement-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftMovementAction ?? '';
    const w = window as MovementWindow;
    if (action === 'preferred-speed') {
        const speedTypeId = el.dataset.ftSpeedTypeId ?? '';
        const casterId = el.dataset.ftCasterId
            || el.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId
            || '';
        if (speedTypeId !== '') { event.preventDefault(); w.updatePreferredSpeedType?.(speedTypeId, casterId); }
    } else if (action === 'ability-click') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        if (abilityId !== '') {
            event.preventDefault();
            // Mount a Creature / Force Mount are creature-gated Reach abilities: arm the action bar's
            // creature-targeting fold-out when the FT3D dock is up instead of firing blind (owner 2026-08-27).
            const casterId = el.closest<HTMLElement>('[data-ft-caster-id]')?.getAttribute('data-ft-caster-id') || '';
            if (w.ftDispatchAbilityClick?.(casterId, abilityId) === true) return;
            // Cast AS the tile's creature, not whichever one happens to be active (action-bar.ts ftLegacyAbilityClickAs).
            if (w.ftLegacyAbilityClickAs) w.ftLegacyAbilityClickAs(casterId, abilityId);
            else w.handleAbilityBtnSingleClick?.(abilityId);
        }
    } else if (action === 'haven-return') {
        event.preventDefault();
        if (!w.ftGeometryRelocation) { refuseUnported('relocation:browser-not-loaded'); return; }
        void w.ftGeometryRelocation({ action: 'haven-return', haven: el.dataset.ftHaven ?? '',
            campaign_id: el.dataset.ftCampaignId ?? '', actor_id: el.dataset.ftTargetId ?? '' });
    } else if (action === 'collision-recovery') {
        event.preventDefault();
        if (el.hasAttribute('disabled')) return;
        refuseUnported('collision-recovery:not-ported', {
            encounterId: el.dataset.ftEncounterId ?? '',
            actorId: el.dataset.ftTargetId ?? '',
        });
    }
});

// ── StatusEffect UUIDs (includes/catalogs/StatusEffect.php) ──
const ST = {
    prone: 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA',
    piloting: 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12',
    riding: 'FF8D219A-2A83-0EE5-A222-E748753E8D0B',
    ridden: 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40',
    piloted: 'DDC66C31-4093-84AF-18BB-093D924B8738',
    sneaking: '1BA5473E-6218-6688-1C68-DCA4FEF00A97',
    hidden: '47667406-1B72-9D60-310B-D8CF6BFE397D',
    paralyzed: 'BF31EE0A-5B3C-370B-0118-96B067CFD929',
    grappling: 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD',
    grappled: '0C2A8037-B0BC-8300-C818-198881B00E7D',
    restrained: 'E34AF232-5438-88FC-9A9F-73850CCCFD51',
    immobile: 'F40E19A3-FC86-187F-050E-8C8B69761353',
    crippled: '99C862B9-D6AE-4E18-7803-A6F9B42A88E1',
} as const;

// ── action ability UUIDs (literal in the PHP) ──
const AB = {
    start_sneaking: 'D047CED2-4272-DB88-52CF-AE7E7F7F2A42',
    stop_sneaking: '3AF662E9-78DD-AC6D-180B-E542217A0372',
    hide: 'C7326C89-CD32-C8C8-2DA9-29D66FFC2284',
    go_prone: 'D5E28A61-E82E-4BE2-AA28-5FC18094591D',
    stand_up: '21D8FBA4-AD2E-EA72-965D-E0445CBFF722',
    evade: 'BE4CCA95-33F0-323D-BA54-844D2540C0F0',
    dodge: '62323E36-5B5E-0114-2750-ED864773B062',
    pilot_creature: 'B308F61E-0CB8-BBCE-1A15-1949322FC836',
    mount_creature: '89C94695-D8C3-B00F-CC8B-FED7398F64FC',
    force_pilot: 'CB7B7214-E388-49CA-BD70-D22FAC23F482',
    force_mount: 'C6AE47C7-BFE1-AE98-0DB3-1E6C9F9FB3E7',
    dismount: '69046DF3-CF81-45F6-BBC8-C237D2B38F5B',
    pilot_vehicle: '6B67881A-D146-271C-CFD9-C2329FBE6C2E',
    ride_vehicle: '3C6E40D1-B142-4C58-0069-43CBA33D4D8C',
    exit_vehicle: '40FECD83-9B94-96AB-4601-EBF9B081C7E3',
    buck_riders: '4476C212-A776-CF84-67A6-5E79C77CB381',
} as const;

const SPEEDS: ReadonlyArray<{ v: string; always?: boolean }> = [
    { v: 'speed_walking', always: true }, { v: 'speed_jumping' }, { v: 'speed_climbing' },
    { v: 'speed_swimming' }, { v: 'speed_flying' }, { v: 'speed_hovering' }, { v: 'speed_burrowing' },
    { v: 'teleport' },
];

function num(v: unknown): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : 0;
}
// Gold/amber "highlighter" for the preferred speed button — matches the preferred
// speed BADGE in the summary bar (tabs/summary.ts) / creature card (creature-summary.ts).
const PREFERRED_SPEED_BTN_STYLE = 'background:linear-gradient(135deg,#f2a900,#ffd633);box-shadow:0 0 0 2px rgba(255,196,0,.6);border-color:#ffe893;';
/** Normalize a speed-type pointer to its UUID. preferred_speed_type can arrive as a
 *  nested {items:{item_id}} object (the reshaped blob), a {item_id} shell, a [uuid]
 *  array, or a bare uuid string — pull the item_id out of whichever shape it is. */
function speedTypeId(v: unknown): string {
    if (v == null) return '';
    const u = Array.isArray(v) ? v[0] : v;
    if (u && typeof u === 'object') {
        const o = recordOf(u);
        const items = recordOf(o?.items);
        const id = items?.item_id ?? o?.item_id;
        return String((Array.isArray(id) ? id[0] : id) ?? '');
    }
    return String(u ?? '');
}
/** Display NAME out of a catalog shell ({items:{item_id,item_name,item_type}}) — the shape
 *  build_campaign_arr / build_encounter_arr write for campaign_light, campaign_sun_state,
 *  campaign_light_cycle_mode and encounter_light. '' for a bare-UUID column (no name rode along),
 *  so callers fall back to their own catalog. speedTypeId() is the matching id reader. */
function shellName(v: unknown): string {
    const items = recordOf(recordOf(scalarOf(v))?.items);
    return String(items?.item_name ?? '').trim();
}
function ec(caster: CachedCaster): Record<string, unknown> {
    return caster.encounter_creatures ?? {};
}

/** Compact read-only position receipt for the Movement tab. Coordinates remain the actor's persisted
 * encounter truth; story membership is still derived by the authority documented in docs/FT3D_FLOORS.md. */
export function renderEncounterPositionHtml(caster: CachedCaster, compact = false): string {
    const targetId = String(scalarOf(ec(caster).encounter_creature_id) ?? '').trim();
    const canView = (typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster))
        || (typeof ftHasCampaignPermission === 'function' && ftHasCampaignPermission(caster));
    if (!targetId || !canView) return '';

    const pretty = (value: number): string => Number.isInteger(value)
        ? String(value)
        : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    const badge = (key: 'x_pos' | 'y_pos' | 'z_pos', label: string): string => {
        const value = pretty(movementCoordinate(caster, key));
        return `<span class="badge bg-dark border d-inline-flex align-items-center gap-1" title="${label}: ${escapeHtml(value)} ft">${iconMap(key, 15, 15, false)}<span>${escapeHtml(value)} ft</span></span>`;
    };
    const classes = compact
        ? 'ft-movement-position ft-movement-position--action-bar d-flex flex-wrap align-items-center gap-2'
        : 'ft-movement-position d-flex flex-wrap align-items-center gap-2 px-2 pt-2';
    return `<div class="${classes}" aria-label="Encounter position">`
        + `<span class="small text-secondary fw-semibold">Position</span>`
        + badge('x_pos', 'X') + badge('y_pos', 'Y') + badge('z_pos', 'Elevation')
        + `</div>`;
}

function movementCoordinate(caster: CachedCaster, key: 'x_pos' | 'y_pos' | 'z_pos'): number {
    const fromReader = typeof window.ftPosCoord === 'function' ? window.ftPosCoord(caster, key) : undefined;
    const position = recordOf(caster.position);
    const raw = fromReader ?? position?.[key] ?? ec(caster)[key] ?? caster[key];
    const value = Number(scalarOf(raw));
    return Number.isFinite(value) ? value : 0;
}

function collisionRecoveryControl(caster: CachedCaster, compact = false): string {
    const targetId = String(scalarOf(ec(caster).encounter_creature_id) ?? '').trim();
    const encounterId = casterEncounterId(caster);
    const campaignId = casterCampaignId(caster);
    if (!targetId || !encounterId || !campaignId) return '';
    const attrs = movementAttrs('collision-recovery', { campaignId, encounterId, targetId });
    if (compact) {
        return `<div class="p-1"><button type="button" class="btn btn-warning text-dark" ${attrs} disabled aria-disabled="true"
            title="Collision recovery is not yet supported in the browser">${iconMap('refresh', 25, 25, 'Collision recovery is not yet supported in the browser')}</button></div>`;
    }
    return `<div class="m-2 p-2 border rounded" data-ft-collision-recovery-control>
        <button type="button" class="btn btn-warning text-dark" ${attrs} disabled aria-disabled="true">${iconMap('refresh', 25, 25, false)} Move to nearest clear spot</button>
        <small class="d-block text-secondary mt-1">Collision recovery is not yet supported in the browser.</small>
    </div>`;
}
/** speed total via the computed.creature.speeds bridge (ftLayerValue doesn't bridge). */
function speedTotal(caster: CachedCaster, variable: string): number {
    const short = variable === 'teleport' ? 'teleport' : variable.slice(6);
    const slot = caster?.computed?.creature?.speeds?.[short];
    if (slot === undefined || slot === null) return 0;
    return num(recordOf(slot)?.total ?? slot);
}
const walk = (c: CachedCaster): number => speedTotal(c, 'speed_walking');
function statusHas(caster: CachedCaster, id: string): boolean {
    return ftCreatureStatuses(caster, 'target').some((e) => String(ftItemsCol(e, 'item_id') ?? '') === id);
}
function maintainsAny(caster: CachedCaster): boolean {
    return ftCreatureStatuses(caster, 'caster').length > 0;
}
/** This creature is itself a MOUNT (being ridden/piloted) — a mount can't initiate mounting/piloting
 *  another creature or vehicle, so those "initiate" buttons hide. Ridden/Piloted live in the TARGET
 *  status bucket (the mount keeps NO caster-side status), which is exactly why the old `!maintainsAny`
 *  short-circuit wrongly kept the buttons visible for a ridden creature. */
function beingMounted(caster: CachedCaster): boolean {
    return statusHas(caster, ST.ridden) || statusHas(caster, ST.piloted);
}
/** True when THIS creature is Ridden/Piloted by a rider on an OPPOSING team — the Buck trigger (throw a
 *  hostile forced-mounter off; a WILLING same-team mount must NOT show it). All truth from the encounterArr
 *  cache: the rider is the Ridden/Piloted status row's status_caster_id; teams via window.teamFromCacheById
 *  (the WS-updated cache, keyed by encounter_creature_id) + window.isOpposingTeamPair — no PHP, no data-*.
 *  Fail-closed: no resolvable hostile rider → no button. */
function hostileRider(caster: CachedCaster): boolean {
    const myEc = String(scalarOf(ec(caster).encounter_creature_id) ?? '').trim();
    if (!myEc) return false;
    const teamOf = window.teamFromCacheById;
    const opposing = window.isOpposingTeamPair;
    if (typeof teamOf !== 'function' || typeof opposing !== 'function') return false;
    const myTeam = teamOf(myEc);
    const statuses: unknown[] = ftCreatureStatuses(caster, 'target');
    for (const row of statuses) {
        const sid = String(ftItemsCol(row, 'item_id') ?? '');
        if (sid !== ST.ridden && sid !== ST.piloted) continue;
        const raw = recordOf(row)?.status_caster_id;
        const riderId = String((Array.isArray(raw) ? raw[0] : raw) ?? '').trim();
        if (riderId === '') continue;
        if (opposing(myTeam, teamOf(riderId))) return true;
    }
    return false;
}
/** Defensive nested-arr field read (bucket → computed → top-level), unwrapping [0]. */
function nestField(arr: unknown, bucket: string, key: string): unknown {
    const root = recordOf(arr);
    if (!root) return undefined;
    const directBucket = recordOf(root[bucket]);
    const computed = recordOf(root.computed);
    const computedBucket = recordOf(computed?.[bucket.replace(/s$/, '')]);
    const v = directBucket?.[key] ?? computedBucket?.[key] ?? root[key];
    return Array.isArray(v) ? v[0] : v;
}
const campField = (camp: unknown, key: string): unknown => nestField(camp, 'campaigns', key);
const encField = (enc: unknown, key: string): unknown => nestField(enc, 'encounters', key);
// caster_arr and encounter_arr/campaign_arr are SEPARATE now — finalize_for_client unsets the nested
// encounter_arr/campaign_arr (ft_object_api_inc.php), so they travel as their OWN cache entries. Resolve
// the live encounter/campaign from those dedicated caches (keyed by the ids on the caster), instead of a
// nested copy. (caster.encounter_arr/campaign_arr kept as a defensive fallback only.)
function unwrapId(v: unknown): string {
    return String((Array.isArray(v) ? v[0] : v) ?? '');
}
function casterEncounterId(caster: CachedCaster): string {
    return unwrapId(ec(caster).encounter_id);
}
function casterCampaignId(caster: CachedCaster): string {
    const pc = caster.player_characters;
    const direct = unwrapId(pc?.attached_campaign_id ?? ec(caster).campaign_id);
    if (direct) return direct;
    // Monsters carry NO campaign pointer on their own blob (owner-flagged 2026-06-10: the
    // encounter X/Z sliders never rendered for them) — resolve via their
    // encounter's campaign, then the page context (ftGetCampaignId / the ft-boot campaign global).
    const viaEnc = unwrapId(encField(resolveEncounter(caster), 'campaign_id'));
    if (viaEnc) return viaEnc;
    const page = String(window.ftGetCampaignId?.()
        || window.FateTide?.campaign?.id
        || '');
    return typeof checkIfValidUUID === 'function' && checkIfValidUUID(page) ? page : '';
}
function resolveEncounter(caster: CachedCaster): unknown {
    const id = casterEncounterId(caster);
    if (!id) return caster.encounter_arr || null;
    return window.FateTide?.encounterCache?.get(id) || caster.encounter_arr || null;
}
/** The halo follows the same encounter projection as FT3D during cache handover.
 * An explicit out-of-combat projection also wins, so exit combat cannot leave a
 * stale End Turn button behind. Never borrow another encounter's retained state. */
function haloEncounter(caster: CachedCaster): unknown {
    const id = casterEncounterId(caster).trim().toUpperCase();
    const retained = (window as Window & {
        ft3dEncounterArrCache?: { encounterId?: unknown; arr?: unknown } | null;
    }).ft3dEncounterArrCache;
    const arr = recordOf(retained?.arr);
    if (id && String(retained?.encounterId ?? '').trim().toUpperCase() === id
        && arr && String(encField(arr, 'encounter_id') ?? '').trim().toUpperCase() === id) return arr;
    return resolveEncounter(caster);
}
function resolveCampaign(caster: CachedCaster): unknown {
    const id = casterCampaignId(caster);
    return (id && window.FateTide?.campaignCache?.get(id)) || caster.campaign_arr || null;
}
/** The campaign clock's hour/minute as the widget shows it. ⛔ While the derived clock is live the cached
 *  `campaign_time_*` columns are only its CHECKPOINT BASE (moved by a checkpoint/push landing), so reading
 *  them left the disc minutes behind the Campaign time widget whenever the GM ran or pushed time. The
 *  derived runtime (campaign-clock-runtime.ts) is the truth the widget paints; the columns are the
 *  fallback for a non-derived campaign. null = no clock known. */
function campaignClockHm(camp: unknown): { hour: number; minute: number } | null {
    try {
        const clock = (window as unknown as { ftCampaignClock?: { isDerived?: () => boolean; getDerived?: () => { cols?: { hour?: unknown; minute?: unknown } } | null } }).ftCampaignClock;
        const cols = clock?.isDerived?.() ? clock.getDerived?.()?.cols : null;
        const campId = String(scalarOf(campField(camp, 'campaign_id')) ?? '').trim().toUpperCase();
        const runtimeId = String(window.FateTide?.campaign?.id ?? '').trim().toUpperCase();
        if (cols && Number.isFinite(Number(cols.hour)) && (!campId || !runtimeId || campId === runtimeId)) {
            return { hour: ((Number(cols.hour) % 24) + 24) % 24, minute: ((Number(cols.minute) % 60) + 60) % 60 };
        }
    } catch { /* fall back to the cached columns */ }
    const rawHour = campField(camp, 'campaign_time_hour');
    if (rawHour === undefined || rawHour === null || rawHour === '') return null;
    return { hour: ((num(rawHour) % 24) + 24) % 24, minute: ((num(campField(camp, 'campaign_time_minute')) % 60) + 60) % 60 };
}
/** Is the caster's ENCOUNTER in combat? Exported because in_combat is an `encounters` column, NOT
 *  an encounter_creatures one — reading caster.encounter_creatures.in_combat looks plausible and is
 *  always falsy. Resolving it needs the encounter cache → nested-fallback chain above, so
 *  every consumer shares this one reader rather than re-deriving it (weapon-abilities.ts gates the
 *  Machinist orb strip on it; orbs are combat-scoped). */
export function casterIsInCombat(caster: CachedCaster): boolean {
    return num(encField(resolveEncounter(caster), 'in_combat')) === 1;
}
const inCombat = casterIsInCombat;
function casterTeam(caster: CachedCaster): string {
    const t = ec(caster).team;
    if (t && typeof t === 'object') return String(ftItemsCol(t, 'item_id') ?? '');
    return String(t ?? '');
}

// ── render_speed_btn port ──
function speedBtn(caster: CachedCaster, variable: string, canEdit: boolean): string {
    countMovementPaneWork('speedBuilders');
    const cat = window.SPEEDTYPE_CATALOG?.[variable];
    if (!cat || !cat.item_id) return '';
    const uuid = String(cat.item_id);
    const prefId = speedTypeId(ec(caster).preferred_speed_type) || speedTypeId(ftLayerValue(caster, 'default_preferred_speed_type'));
    const isPreferred = prefId !== '' && prefId === uuid;
    let cls = 'btn text-dark' + (isPreferred ? '' : ' btn-info');
    const styleAttr = isPreferred ? ` style="${PREFERRED_SPEED_BTN_STYLE}"` : '';
    // Pooled movement (server: movement_pool.php): every speed button reads the ONE banked
    // fraction and prints how many feet of ITS speed that fraction buys.
    const remaining = movementFeetFor(caster, variable);
    const hasBadge = inCombat(caster) && remaining > 0;
    if (hasBadge) cls += ' position-relative';
    const casterId = String(ec(caster).encounter_creature_id ?? '').trim();
    const action = canEdit ? ` ${movementAttrs('preferred-speed', { speedTypeId: uuid, casterId })}` : ' disabled';
    const badge = hasBadge ? `<span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">${escapeHtml(String(remaining))}</span>` : '';
    return `<div class="p-1" data-ft-movement-speed="${variable}"><button type="button" class="${cls}"${styleAttr}${action}>${iconMap(variable)}${badge}</button></div>`;
}

// ── simple action button (handleAbilityBtnSingleClick) ──
function actBtn(icon: string, abilityId: string): string {
    return `<div class="p-1" data-ft-ability-id="${abilityId}"><button class="btn btn-info text-dark" ${movementAbilityAttrs(abilityId)} data-ability_id="${abilityId}">${iconMap(icon)}</button></div>`;
}

/** Pilot / Mount / Force-Pilot / Force-Mount stay icon-only (label rides in the tooltip — a visible
 *  caption made the buttons too fat); the FORCE variants are painted bg-danger because they're a
 *  hostile/forced action against another creature. They're wrapped in a bordered grouping box at the
 *  call sites so the cluster reads as one "mount" control. Shared by the tab + the action-bar strip. */
function mountBtn(icon: string, abilityId: string, label: string, danger: boolean): string {
    const cls = danger ? 'btn btn-danger' : 'btn btn-info text-dark';
    return `<div class="p-1" data-ft-ability-id="${abilityId}"><button class="${cls}" ${movementAbilityAttrs(abilityId)} data-ability_id="${abilityId}">${iconMap(icon, 25, 25, label)}</button></div>`;
}

function hideBtn(caster: CachedCaster): string {
    if (!stealthV2Enabled()) {
        const e = ec(caster);
        return `<div class="p-1" data-ft-ability-id="${AB.hide}"><button class="btn btn-info text-dark use-ability-range"
            data-ability_id="${AB.hide}" data-item_type="quick_ability" data-ap_required="1"
            data-range="D82855E1-4BB9-93EA-6374-B9904318BC1D" data-aoe_type="981032B8-4F0B-43C9-8D98-67C693E4DB42"
            data-aoe_distance="20" data-aoe_distance_height="" data-persistent_aoe="0" data-aoe_follows_target="0"
            data-roll_type="AD80D31D-898B-66CE-0894-1335D11B1529"
            data-effect_only_affects_same_team_type="0" data-effect_only_affects_opposing_team_type="0"
            data-caster_team="${escapeHtml(casterTeam(caster))}" data-caster_in_combat="${inCombat(caster) ? '1' : '0'}"
            data-caster_aps="${escapeHtml(String(e.aps ?? '0'))}" data-caster_reaction="${escapeHtml(String(e.reaction ?? '0'))}"
            data-caster_mana="${escapeHtml(String(e.mana ?? '0'))}">${iconMap('hide_creature', 25, 25, 'Hide')}</button></div>`;
    }
    return `<div class="p-1" data-ft-ability-id="${AB.hide}"><button class="btn btn-info text-dark" ${movementAbilityAttrs(AB.hide)}
        data-ability_id="${AB.hide}">${iconMap('hide_creature', 25, 25, 'Hide')}</button></div>`;
}

/** STEALTH tile for the FT3D dock's left cluster (owner 2026-08-27). Stealth v2 ruling §A.1
 *  (plans/ft3d-stealth-redesign-2026-08-27.md): `sneaking` is retired, `hidden` IS the stance and Hide
 *  is what "start sneaking" was — so this is ONE tile, no Sneak toggle. Same click path as the movement
 *  tab's Hide (direct stance action; the server enforces the re-hide-lockout rules,
 *  and no longer wants a sneaking prerequisite). Latched + inert while hidden: the ruling has no
 *  voluntary exit — you leave the stance by being spotted or by a GM reveal. '' when not editable. */
export function renderStealthRow(caster: CachedCaster): string {
    if (!stealthV2Enabled()) return '';
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    if (!canEdit) return '';
    const has = (id: string) => statusHas(caster, id);
    if (has(ST.hidden)) {
        // TOGGLE (owner 2026-08-27): while hidden the tile wears the stop_sneaking glyph on a tinted
        // background and a click BREAKS STEALTH through the creature console's existing reveal action
        // (options/tools/encounter_creatures/reveal_encounter_creature.php strips the hidden status).
        const ecId = String(caster?.encounter_creatures?.encounter_creature_id ?? '').trim();
        return `<button type="button" class="ft-action-hands__tile is-hidden" data-ft-creature-console-action="reveal" data-ft-ec-id="${escapeHtml(ecId)}" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Hidden — click to break stealth">${iconMap('stop_sneaking', 25, 25, 'Break stealth')}</button>`;
    }
    const blocked = walk(caster) <= 0 || has(ST.grappled) || has(ST.restrained) || has(ST.grappling);
    if (blocked) {
        return `<button type="button" class="ft-action-hands__tile" disabled data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Hide">${iconMap('hide_creature', 25, 25, 'Hide')}</button>`;
    }
    return hideBtn(caster).replace(/^<div class="p-1"[^>]*>/, '').replace(/<\/div>$/, '')
        .replace('class="btn btn-info text-dark', 'class="ft-action-hands__tile');
}

/** Wrap a group's buttons in its own flex row (one cluster per line; borderless — the
 *  border boxes were tried 2026-06-10 and owner-rejected as border hell; the row break +
 *  spacing alone is the containment). Only when at least one button exists. */
function group(buttons: string): string {
    if (!buttons.includes('<button')) return '';
    return `<div class="ft-core-ability-group ft-core-ability-group--actions d-flex flex-wrap align-items-center gap-1 mb-2">${buttons}</div>`;
}

/** Compact movement-action strip for the action-bar island's "Movement" fly-up: the preferred-speed
 *  selector + the stance / mount / vehicle action buttons — the SAME set + gating as the Movement tab's
 *  action column below, minus the X/Z position sliders + haven travel. Returns a flat list of buttons
 *  (each a `.p-1 > button`) for the flyout to flex-wrap. (Gating mirrors renderMovement; kept in sync.) */
export function renderMovementActionStrip(caster: CachedCaster): string {
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const grappledOrRestrained = statusHas(caster, ST.grappled) || statusHas(caster, ST.restrained);
    const w = walk(caster);
    const has = (id: string) => statusHas(caster, id);
    const any = maintainsAny(caster);

    // Preferred-speed-type selector — collected into ONE grouped cluster (a bordered segmented unit) so it
    // reads as a single "preferred movement type" control, set apart from the stance/mount/vehicle ACTIONS
    // that follow (owner 2026-06-26: group the preferred speed-type logic together).
    let speeds = '';
    for (const s of SPEEDS) {
        if (s.always || speedTotal(caster, s.v) > 0) speeds += speedBtn(caster, s.v, canEdit);
    }

    let actions = '';
    if (canEdit) {
        const showStanceRow = !grappledOrRestrained || (w > 0 && any && has(ST.sneaking));
        if (showStanceRow) {
            // Stealth v2 (owner 2026-08-28): `sneaking` is RETIRED — Hide is the stance; the two
            // Start/Stop Sneaking abilities are soft-deleted in the catalog.
            // Advisory only: Janus checks every hostile's current perception.
            if (w > 0 && (stealthV2Enabled() || has(ST.sneaking)) && !has(ST.hidden) && !has(ST.grappling) && !has(ST.grappled) && !has(ST.restrained)) actions += hideBtn(caster);
            if (w > 0 && (!any || (!has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.crippled) && !has(ST.immobile)))) actions += actBtn('go_prone', AB.go_prone);
            if (w > 0 && has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.crippled) && !has(ST.immobile)) actions += actBtn('stand_up', AB.stand_up);
            const evadeEligible = w > 0 && (!any || (!has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && !has(ST.paralyzed)));
            actions += `<div class="p-1">${renderSimpleTriggerAbilityButton(AB.evade, 'btn btn-info text-dark', iconMap('evade'), caster, { title: 'Evade', isDisabled: !evadeEligible })}</div>`;
            if (!any || (w > 0 && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && !has(ST.paralyzed))) actions += actBtn('dodge', AB.dodge);
        }
        if (!grappledOrRestrained) {
            const mounted = beingMounted(caster);
            // A MOUNT can't initiate mounting — `!mounted` gates OUTSIDE the `!any || (...)` short-circuit.
            const mountOk = !mounted && (!any || (w > 0 && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && !has(ST.riding) && !has(ST.piloting) && !has(ST.paralyzed)));
            const dismountOk = (id: string) => any && !has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && has(id);
            const buckOk = !has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && hostileRider(caster);
            if (mountOk) {
                const mc = mountBtn('pilot_a_creature', AB.pilot_creature, 'Pilot a Creature', false)
                    + mountBtn('mount_a_creature', AB.mount_creature, 'Mount a Creature', false)
                    + mountBtn('force_pilot_a_creature', AB.force_pilot, 'Force Pilot a Creature', true)
                    + mountBtn('force_mount_a_creature', AB.force_mount, 'Force Mount a Creature', true);
                actions += `<div class="ft-move-mount-group d-inline-flex flex-wrap align-items-center border rounded px-1 me-2">${mc}</div>`;
            }
            // Exit/counter button (exactly one): Buck (hostile forced-mounter — the mount throws them off) takes
            // priority over Dismount, which is the RIDER voluntarily getting off (piloting → Exit Vehicle;
            // riding/ridden → Dismount). A force-mounted creature is the MOUNT, so it gets Buck, not "Dismount".
            if (buckOk) actions += actBtn('buck_creature', AB.buck_riders);
            // Dismount = the RIDER voluntarily getting off (Riding only). A Ridden creature is the MOUNT,
            // not a rider — it can't "dismount" (its counter is Buck, above). Piloting → Exit Vehicle below.
            else if (dismountOk(ST.riding)) actions += actBtn('dismount', AB.dismount);
            if (mountOk) {
                const vc = actBtn('pilot_a_vehicle', AB.pilot_vehicle) + actBtn('ride_a_vehicle', AB.ride_vehicle);
                actions += `<div class="ft-move-vehicle-group d-inline-flex flex-wrap align-items-center border rounded px-1 me-2">${vc}</div>`;
            }
            if (dismountOk(ST.piloting)) actions += actBtn('exit_vehicle', AB.exit_vehicle);
        }
    }

    // Haven travel (campsite / stronghold) — the "return to camp" core button, nested into the
    // Movement fly-up because you are literally MOVING to camp (owner direction 2026-06-29). PC-only +
    // out-of-combat, same gating as the Movement tab; returns flat .p-1>button items to flow inline.
    let haven = '';
    if (canEdit && isPlayerCharacter(caster)) {
        haven = havenButtons(caster, resolveCampaign(caster), { wrap: false });
    }

    let html = '';
    if (speeds) html += `<div class="ft-move-speed-group d-inline-flex flex-wrap align-items-center border rounded px-1 me-2">${speeds}</div>`;
    if (actions) html += actions;
    if (canEdit) html += collisionRecoveryControl(caster, true);
    if (haven) html += haven;
    return html;
}

type MovementGroup = 'stance' | 'mount' | 'vehicle';

// Builders run only when the group's scalar inputs change; action/target delegation stays shared.
function movementActionGroup(caster: CachedCaster, kind: MovementGroup): string {
    countMovementPaneWork('groupBuilders');
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const grappledOrRestrained = statusHas(caster, ST.grappled) || statusHas(caster, ST.restrained);
    const w = walk(caster);
    const has = (id: string) => statusHas(caster, id);
    const any = maintainsAny(caster);

    if (!canEdit) return '';
    if (kind === 'stance') {
        // stance group
        const showStanceRow = !grappledOrRestrained || (w > 0 && any && has(ST.sneaking));
        if (showStanceRow) {
            let b = '';
            // Stealth v2 (owner 2026-08-28): `sneaking` is RETIRED — Hide is the stance; the two
            // Start/Stop Sneaking abilities are soft-deleted in the catalog.
            // Advisory only: Janus checks every hostile's current perception.
            if (w > 0 && (stealthV2Enabled() || has(ST.sneaking)) && !has(ST.hidden) && !has(ST.grappling) && !has(ST.grappled) && !has(ST.restrained)) b += hideBtn(caster);
            if (w > 0 && (!any || (!has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.crippled) && !has(ST.immobile)))) b += actBtn('go_prone', AB.go_prone);
            if (w > 0 && has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.crippled) && !has(ST.immobile)) b += actBtn('stand_up', AB.stand_up);
            // Evade remains visible, disabled when ineligible.
            const evadeEligible = w > 0 && (!any || (!has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && !has(ST.paralyzed)));
            b += `<div class="p-1" data-ft-ability-id="${AB.evade}">${renderSimpleTriggerAbilityButton(AB.evade, 'btn btn-info text-dark', iconMap('evade'), caster, { title: 'Evade', isDisabled: !evadeEligible })}</div>`;
            if (!any || (w > 0 && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && !has(ST.paralyzed))) b += actBtn('dodge', AB.dodge);
            return group(b);
        }
        return '';
    }
    if (!grappledOrRestrained) {
        // mount/creature group
        const mounted = beingMounted(caster);
        // A MOUNT can't initiate mounting — `!mounted` gates OUTSIDE the `!any || (...)` short-circuit.
        const mountOk = !mounted && (!any || (w > 0 && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && !has(ST.riding) && !has(ST.piloting) && !has(ST.paralyzed)));
        const dismountOk = (id: string) => any && !has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && has(id);
        if (kind === 'mount') {
            const buckOk = !has(ST.prone) && !has(ST.grappled) && !has(ST.restrained) && !has(ST.immobile) && !has(ST.crippled) && hostileRider(caster);
            let m = '';
            if (mountOk) {
                const mc = mountBtn('pilot_a_creature', AB.pilot_creature, 'Pilot a Creature', false)
                    + mountBtn('mount_a_creature', AB.mount_creature, 'Mount a Creature', false)
                    + mountBtn('force_pilot_a_creature', AB.force_pilot, 'Force Pilot a Creature', true)
                    + mountBtn('force_mount_a_creature', AB.force_mount, 'Force Mount a Creature', true);
                m += `<div class="ft-move-mount-group d-inline-flex flex-wrap align-items-center border rounded px-1 me-2">${mc}</div>`;
            }
            // Exit/counter button (exactly one): Buck (hostile forced-mounter — the mount throws them off) takes
            // priority over Dismount, the RIDER voluntarily getting off (piloting → Exit Vehicle; riding/ridden →
            // Dismount). A force-mounted creature is the MOUNT, so it gets Buck, not a "Dismount" it can't do.
            if (buckOk) m += actBtn('buck_creature', AB.buck_riders);
            // Dismount = the RIDER voluntarily getting off (Riding only). A Ridden creature is the MOUNT,
            // not a rider — it can't "dismount" (its counter is Buck, above). Piloting → Exit Vehicle below.
            else if (dismountOk(ST.riding)) m += actBtn('dismount', AB.dismount);
            return group(m);
        }
        // vehicle group
        let v = '';
        if (mountOk) {
            const vc = actBtn('pilot_a_vehicle', AB.pilot_vehicle) + actBtn('ride_a_vehicle', AB.ride_vehicle);
            v += `<div class="ft-move-vehicle-group d-inline-flex flex-wrap align-items-center border rounded px-1 me-2">${vc}</div>`;
        }
        if (dismountOk(ST.piloting)) v += actBtn('exit_vehicle', AB.exit_vehicle);
        return group(v);
    }
    return '';
}

type MovementPart = { key: string; reads: readonly unknown[]; html: () => string };

/** Retain primitive display inputs only. Reads share the builders' lean/wrapped field readers. */
function movementParts(caster: CachedCaster): MovementPart[] {
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const canView = canEdit || (typeof ftHasCampaignPermission === 'function' && ftHasCampaignPermission(caster));
    const gates = [canEdit, walk(caster) > 0, maintainsAny(caster)];
    const stance = [ST.grappled, ST.restrained, ST.sneaking, ST.hidden, ST.grappling, ST.prone, ST.crippled, ST.immobile, ST.paralyzed]
        .map(id => statusHas(caster, id));
    const carrier = [ST.grappled, ST.restrained, ST.prone, ST.immobile, ST.crippled, ST.riding, ST.piloting, ST.paralyzed, ST.ridden, ST.piloted]
        .map(id => statusHas(caster, id));
    const actor = String(scalarOf(ec(caster).encounter_creature_id) ?? '').trim();
    const encounter = casterEncounterId(caster), campaign = casterCampaignId(caster), camp = resolveCampaign(caster);
    // Trigger rows mutate in place: retain reader results rather than object identities.
    const trigger = Array.isArray(caster.triggers) ? caster.triggers.find(row =>
        speedTypeId(ftLayerValue(row, 'trigger_ability_id')).toUpperCase() === AB.evade) : undefined;
    const triggerActive = !trigger || Number(ftLayerValue(trigger, 'trigger_inactive') ?? 0) === 0;
    const stealth = stealthV2Enabled();
    return [
        { key: 'position', reads: [canView, actor, ...(['x_pos', 'y_pos', 'z_pos'] as const).map(key => movementCoordinate(caster, key))], html: () => renderEncounterPositionHtml(caster) },
        { key: 'stance', reads: [...gates, ...stance, stealth, ...(!stealth ? [casterTeam(caster), inCombat(caster),
            String(ec(caster).aps ?? '0'), String(ec(caster).reaction ?? '0'), String(ec(caster).mana ?? '0')] : []), actor, encounter, triggerActive],
            html: () => movementActionGroup(caster, 'stance') },
        { key: 'mount', reads: [...gates, ...carrier, hostileRider(caster)], html: () => movementActionGroup(caster, 'mount') },
        { key: 'vehicle', reads: [...gates, ...carrier], html: () => movementActionGroup(caster, 'vehicle') },
        { key: 'collision', reads: [canEdit, actor, encounter, campaign], html: () => canEdit ? collisionRecoveryControl(caster) : '' },
        { key: 'haven', reads: [canEdit, isPlayerCharacter(caster), !!camp, actor, encounter, campaign, inCombat(caster),
            num(campField(camp, 'campaign_disable_player_haven_return')), speedTypeId(campField(camp, 'campaign_campsite_encounter_id')),
            speedTypeId(campField(camp, 'campaign_stronghold_encounter_id')), String(campField(camp, 'campaign_id') ?? '')],
            html: () => canEdit && isPlayerCharacter(caster) ? havenButtons(caster, camp) : '' },
    ];
}

function movementPartHtml(part: MovementPart): string {
    return `<div data-ft-movement-part="${part.key}" style="display:contents">${part.html()}</div>`;
}

export function renderMovement(caster: CachedCaster, _casterId: string): string {
    countMovementPaneWork('fullBuilders');
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const parts = movementParts(caster);
    const part = (key: string): string => movementPartHtml(parts.find(part => part.key === key)!);
    const speeds = SPEEDS.filter(s => s.always || speedTotal(caster, s.v) > 0).map(s => speedBtn(caster, s.v, canEdit)).join('');
    return `<div class="ft-pc-movement ft-core-abilities border rounded mb-2 px-1">
        <h5 class="text-start ps-3 mt-1 mb-0 py-2 text-light bg-gradient rounded-top">${iconMap('movement')} Movement</h5>
        ${part('position')}
        <div class="px-1 pt-2 d-flex flex-wrap align-items-start gap-2">
            <div style="flex:1 1 320px; min-width:280px;">
                <div data-ft-movement-speeds class="ft-core-ability-group ft-core-ability-group--actions d-flex flex-wrap align-items-center gap-1 mb-2">${speeds}</div>
                ${part('stance')}${part('mount')}${part('vehicle')}
            </div>
            <div data-ft-movement-below style="flex:1 1 360px; min-width:300px;" ${canEdit ? '' : 'hidden'}>${part('collision')}${part('haven')}</div>
        </div>
    </div>`;
}

type MovementPaneState = { root: Element; casterId: string; reads: Map<string, readonly unknown[]> };
const movementPanes = new WeakMap<HTMLElement, MovementPaneState>();
/** Optional smoke counters observe actual work without changing the renderer's decisions. */
function countMovementPaneWork(kind: string): void {
    const counts = (window as Window & { ftMovementPaneCounts?: Record<string, number> }).ftMovementPaneCounts;
    if (counts) counts[kind] = (counts[kind] ?? 0) + 1;
}
const sameMovementReads = (a: readonly unknown[] | undefined, b: readonly unknown[]): boolean =>
    !!a && a.length === b.length && a.every((value, index) => value === b[index]);
const hydrateMovement = (root: HTMLElement): void => {
    countMovementPaneWork('hydrations');
    window.ftMountRuneIcons?.(root); window.ftMountIcons?.(root);
};
function patchSpeedSelection(button: HTMLButtonElement, selected: boolean): void {
    if (button.classList.contains('btn-info') === selected) button.classList.toggle('btn-info', !selected);
    if (selected && button.getAttribute('style') !== PREFERRED_SPEED_BTN_STYLE) button.setAttribute('style', PREFERRED_SPEED_BTN_STYLE);
    else if (!selected && button.hasAttribute('style')) button.removeAttribute('style');
}

/** A preference changes only selection; the dock flyout keeps all speed controls and their icons. */
export function patchPreferredSpeedButtons(caster: CachedCaster, container: HTMLElement): void {
    const preferred = speedTypeId(ec(caster).preferred_speed_type) || speedTypeId(ftLayerValue(caster, 'default_preferred_speed_type'));
    container.querySelectorAll<HTMLElement>('[data-ft-movement-speed]').forEach(row => {
        const button = row.querySelector('button');
        const id = window.SPEEDTYPE_CATALOG?.[row.dataset.ftMovementSpeed ?? '']?.item_id;
        if (button) patchSpeedSelection(button, !!preferred && preferred === String(id ?? ''));
    });
}

/** First mount builds the pane. Warm updates build only changed control groups;
 * speed fields patch their existing buttons/badges without rebuilding their markup. */
export function renderMovementPane(caster: CachedCaster, casterId: string, container: HTMLElement): void {
    const parts = movementParts(caster);
    let held = movementPanes.get(container);
    const cold = !held || held.casterId !== casterId || held.root !== container.firstElementChild;
    if (cold) {
        replaceHydratedHtml(container, renderMovement(caster, casterId));
        held = { root: container.firstElementChild!, casterId, reads: new Map() };
        movementPanes.set(container, held);
        for (const part of parts) rememberPatchMarkup(container.querySelector<HTMLElement>(`[data-ft-movement-part="${part.key}"]`)!);
        hydrateMovement(container);
    }
    const state = held!;
    for (const part of parts) {
        if (!cold && !sameMovementReads(state.reads.get(part.key), part.reads)) {
            patchMarkup(container.querySelector<HTMLElement>(`[data-ft-movement-part="${part.key}"]`)!, part.html(), hydrateMovement);
        }
        state.reads.set(part.key, part.reads);
    }
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const below = container.querySelector<HTMLElement>('[data-ft-movement-below]')!;
    const belowHidden = !below.querySelector('[data-ft-movement-part] > *');
    if (below.hidden !== belowHidden) below.hidden = belowHidden;
    const speeds = container.querySelector<HTMLElement>('[data-ft-movement-speeds]')!;
    const preferred = speedTypeId(ec(caster).preferred_speed_type) || speedTypeId(ftLayerValue(caster, 'default_preferred_speed_type'));
    for (const [index, speed] of SPEEDS.entries()) {
        const cat = window.SPEEDTYPE_CATALOG?.[speed.v];
        const catId = cat?.item_id ? String(cat.item_id) : '';
        const visible = !!catId && (!!speed.always || speedTotal(caster, speed.v) > 0);
        const selected = preferred !== '' && preferred === catId;
        const remaining = movementFeetFor(caster, speed.v), badgeVisible = inCombat(caster) && remaining > 0;
        const actor = String(ec(caster).encounter_creature_id ?? '').trim();
        const reads = [visible, catId, selected, canEdit, badgeVisible, remaining, actor];
        if (cold || sameMovementReads(state.reads.get(speed.v), reads)) { state.reads.set(speed.v, reads); continue; }
        let row = speeds.querySelector<HTMLElement>(`[data-ft-movement-speed="${speed.v}"]`);
        if (!visible) {
            if (row) { replaceHydratedHtml(row, ''); row.remove(); }
        } else if (!row) {
            const template = document.createElement('template'); template.innerHTML = speedBtn(caster, speed.v, canEdit);
            row = template.content.firstElementChild as HTMLElement;
            const following = SPEEDS.slice(index + 1).map(s => speeds.querySelector(`[data-ft-movement-speed="${s.v}"]`)).find(Boolean) ?? null;
            speeds.insertBefore(row, following); hydrateMovement(row);
        } else {
            const button = row.querySelector('button')!;
            patchSpeedSelection(button, selected); button.classList.toggle('position-relative', badgeVisible);
            button.disabled = !canEdit;
            if (canEdit) {
                button.dataset.ftMovementAction = 'preferred-speed'; button.dataset.ftSpeedTypeId = catId; button.dataset.ftCasterId = actor;
            } else {
                delete button.dataset.ftMovementAction; delete button.dataset.ftSpeedTypeId; delete button.dataset.ftCasterId;
            }
            let badge = button.querySelector<HTMLElement>('.badge');
            if (!badgeVisible) badge?.remove();
            else {
                if (!badge) { badge = document.createElement('span'); badge.className = 'position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'; button.append(badge); }
                if (badge.textContent !== String(remaining)) {
                    if (badge.firstChild?.nodeType === Node.TEXT_NODE) badge.firstChild.nodeValue = String(remaining);
                    else badge.textContent = String(remaining);
                }
            }
        }
        state.reads.set(speed.v, reads);
    }
    countMovementPaneWork(cold ? 'mounts' : 'updates');
}

function havenButtons(caster: CachedCaster, camp: unknown, opts: { wrap?: boolean } = {}): string {
    const permitted = (typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster))
        && camp && num(campField(camp, 'campaign_disable_player_haven_return')) !== 1;
    if (!permitted) return '';
    const isUuid = (s: string) => typeof checkIfValidUUID === 'function' && checkIfValidUUID(s);
    const combat = inCombat(caster);
    const current = String(ec(caster).encounter_id ?? '');
    const campsite = speedTypeId(campField(camp, 'campaign_campsite_encounter_id'));
    const stronghold = speedTypeId(campField(camp, 'campaign_stronghold_encounter_id'));
    const campaignId = String(campField(camp, 'campaign_id') ?? '') || casterCampaignId(caster);
    const ecId = String(ec(caster).encounter_creature_id ?? '');

    // Always render BOTH havens (campsite + stronghold) so the control never silently vanishes
    // (owner 2026-06-29: a disappearing button — e.g. while standing IN the campsite — was confusing).
    // When a haven can't be used right now it renders DISABLED with a bs-tooltip REASON. The tooltip
    // rides the WRAPPER div, not the <button>: a disabled <button> swallows hover events, so a tooltip
    // attached to it never fires — the wrapper still receives the hover and shows the explanation.
    const havenBtn = (encId: string, icon: string, kind: string): string => {
        const set = encId !== '' && isUuid(encId);
        let reason = '';
        if (!set) reason = `No ${kind} has been set for this campaign yet.`;
        else if (combat) reason = `You can't return to your ${kind} during combat.`;
        else if (current === encId) reason = `You're already at your ${kind}.`;
        const label = `Return to ${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
        if (reason === '') {
            return `<div class="p-1"><button type="button" class="btn btn-primary" role="button" ${movementAttrs('haven-return', { campaignId, haven: kind, targetId: ecId })}>${iconMap(icon, 25, 25, true, label)}</button></div>`;
        }
        return `<div class="p-1" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(reason)}">`
            + `<button type="button" class="btn btn-dark border" disabled aria-disabled="true">${iconMap(icon, 25, 25, false)}</button></div>`;
    };
    const b = havenBtn(campsite, 'campaign_campsite', 'campsite')
        + havenBtn(stronghold, 'campaign_stronghold', 'stronghold');
    // wrap === false → flat .p-1>button items for the action-bar Movement strip (flows inline with the
    // mount/stance buttons); default wrapped group for the Movement tab's action column.
    if (opts.wrap === false) return b;
    return `<div class="ft-core-ability-group ft-core-ability-group--actions d-flex flex-wrap align-items-center gap-1 mb-2 mt-2">${b}</div>`;
}

// ── Pooled movement + the End-Turn halo (docs/DERIVED_MEDIUM_MOVEMENT_PLAN_20260827.md, slice 1) ──
//
// `encounter_creatures.movement_pct` is the ONE banked-movement fraction (server:
// includes/functions/janus/encounter/movement_pool.php). 0 = nothing banked this turn; 1.0 = a full
// AP's worth; grants can push it above 1. Feet of speed T it buys = pct × base(T).

/** The banked movement fraction on the caster's encounter row (lean-elided 0 → 0). */
export function casterMovementPct(caster: CachedCaster): number {
    const v = num(ec(caster).movement_pct);
    return v > 0 ? v : 0;
}

/**
 * Fraction the halo should present right now. Movement is banked lazily: when the pool is empty but
 * an AP remains, the first foot automatically spends that AP and opens a fresh full pool. Showing an
 * empty rim in that state told the player they could not move even though the very next drag succeeds.
 */
function haloMovementPct(caster: CachedCaster, speedVariable: string): number {
    if (speedTotal(caster, speedVariable) <= 0) return 0;
    const banked = casterMovementPct(caster);
    if (banked > 1e-9) return banked;
    return num(ec(caster).aps) > 0 ? 1 : 0;
}

/** Feet of speed `variable` the banked pool buys — FLOOR (server pays whole 1-ft units; Codex review). */
export function movementFeetFor(caster: CachedCaster, variable: string): number {
    return Math.floor(casterMovementPct(caster) * speedTotal(caster, variable) + 1e-6);
}

/** Is it this caster's turn in its encounter? */
export function casterIsCurrentTurn(caster: CachedCaster): boolean {
    const enc = resolveEncounter(caster);
    const turnId = String(encField(enc, 'current_encounter_creature_id_turn') ?? '').trim().toLowerCase();
    if (turnId === '') return false;
    const mine = String(ec(caster).encounter_creature_id ?? '').trim().toLowerCase();
    return mine !== '' && mine === turnId;
}

type CurrentTurnActor = { id: string; name: string; pictureUrl: string };

/** Resolve the current-turn actor only from the viewer's retained encounter projection. A private actor
 *  absent from that projection remains anonymous; this presentation helper never fetches around privacy. */
function currentTurnActor(caster: CachedCaster): CurrentTurnActor | null {
    const encounterId = casterEncounterId(caster).trim().toUpperCase();
    const campaignRetained = (window as Window & {
        ft3dEncounterArrCache?: { encounterId?: unknown; arr?: unknown } | null;
    }).ft3dEncounterArrCache;
    const retainedId = String(campaignRetained?.encounterId ?? '').trim().toUpperCase();
    const retained = !encounterId || !retainedId || retainedId === encounterId
        ? recordOf(campaignRetained?.arr) : null;
    const resolved = recordOf(resolveEncounter(caster));
    const directCached = recordOf(window.FateTide?.encounterCache?.get(casterEncounterId(caster)));
    // Search retained snapshots full-first without fetching or changing current-turn authority.
    const snapshots = [retained, directCached, resolved, recordOf(caster.encounter_arr)]
        .filter((snapshot): snapshot is Record<string, unknown> => snapshot !== null);
    // The combat strip is already painted from the authoritative encounter turn pointer. Retain it as
    // a presentation fallback for the split-cache window where the pointer/highlight has arrived but
    // the full creature row has not. It is per-viewer DOM, so it cannot reveal an unprojected actor.
    const snapshotTurnId = snapshots
        .map(snapshot => String(encField(snapshot, 'current_encounter_creature_id_turn') ?? '').trim())
        .find(Boolean) ?? '';
    // Match the authoritative pointer first, then prefer the active combat presentation node.
    const turnThumbs = Array.from(document.querySelectorAll<HTMLElement>(
        '.creature-thumbnail-container.current-turn[data-encounter_creature_id], '
        + '.creature-thumbnail-container.current-turn-combat[data-encounter_creature_id]',
    ));
    const visibleFirst = (a: HTMLElement, b: HTMLElement): number => {
        const score = (node: HTMLElement) => (node.getClientRects().length > 0 ? 2 : 0)
            + (node.classList.contains('current-turn-combat') ? 1 : 0);
        return score(b) - score(a);
    };
    turnThumbs.sort(visibleFirst);
    const turnThumb = (snapshotTurnId
        ? turnThumbs.find(node => String(node.dataset.encounter_creature_id ?? '').trim().toUpperCase()
            === snapshotTurnId.toUpperCase())
        : null) ?? turnThumbs[0] ?? null;
    const thumbId = String(turnThumb?.dataset.encounter_creature_id ?? '').trim();
    const id = snapshotTurnId || thumbId;
    const rows = snapshots.flatMap(snapshot => (
        Array.isArray(snapshot.encounter_creatures_arr) ? snapshot.encounter_creatures_arr : []
    ));
    const actor = rows.find((candidate) => {
        const row = recordOf(candidate);
        const creature = recordOf(row?.encounter_creatures);
        return String(scalarOf(creature?.encounter_creature_id) ?? '').trim().toUpperCase() === id.toUpperCase();
    });
    if (!id) return null;
    if (!actor) {
        if (!turnThumb || thumbId.toUpperCase() !== id.toUpperCase()) return null;
        const thumbImage = turnThumb.querySelector<HTMLImageElement>('img');
        const name = String(
            turnThumb.getAttribute('data-bs-original-title')
            || turnThumb.getAttribute('title')
            || thumbImage?.alt
            || 'Creature'
        ).trim();
        return {
            id,
            name,
            // No cached row: the turn-order thumbnail's own image is the only art there is (letterboxed by the badge).
            pictureUrl: String(thumbImage?.currentSrc || thumbImage?.src || ''),
        };
    }

    const row = recordOf(actor);
    const creature = recordOf(row?.encounter_creatures);
    const player = recordOf(row?.player_characters);
    const item = recordOf(row?.items);
    let name = '';
    try { name = typeof getCreatureName === 'function' ? String(getCreatureName(actor) || '') : ''; } catch { /* fallback below */ }
    if (!name || name === 'N/A') {
        name = String(scalarOf(player?.player_name)
            ?? scalarOf(creature?.encounter_creature_name)
            ?? scalarOf(creature?.creature_name)
            ?? scalarOf(item?.item_name)
            ?? 'Creature');
    }
    let pictureUrl = '';
    try {
        const picture = typeof selectCreaturePicture === 'function'
            ? selectCreaturePicture(actor, { use_picture_thumbnail: true }) : null;
        pictureUrl = picture && typeof pictureField0 === 'function'
            ? String(pictureField0(picture, 'pic_url') || '') : '';
    } catch { /* initials remain a useful privacy-safe fallback */ }
    if (!pictureUrl && turnThumb && thumbId.toUpperCase() === id.toUpperCase()) {
        const thumbImage = turnThumb.querySelector<HTMLImageElement>('img');
        pictureUrl = String(thumbImage?.currentSrc || thumbImage?.src || '');
    }
    return { id, name, pictureUrl };
}

/** The controlled unit's own display name / portrait for the halo core (same readers as the turn actor). */
function casterDisplayName(caster: CachedCaster): string {
    try {
        const n = typeof getCreatureName === 'function' ? String(getCreatureName(caster) || '') : '';
        if (n && n !== 'N/A') return n;
    } catch { /* fall through */ }
    return String(scalarOf(caster.player_characters?.player_name)
        ?? scalarOf(ec(caster).encounter_creature_name)
        ?? scalarOf(caster.items?.item_name)
        ?? '');
}
function casterPortraitUrl(caster: CachedCaster): string {
    try {
        const picture = typeof selectCreaturePicture === 'function'
            ? selectCreaturePicture(caster, { use_picture_thumbnail: true }) : null;
        return picture && typeof pictureField0 === 'function' ? String(pictureField0(picture, 'pic_url') || '') : '';
    } catch { return ''; }
}

function actorInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return (parts.length > 1
        ? `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`
        : (parts[0]?.slice(0, 2) || '??')).toUpperCase();
}

/** The preferred speed variable (`speed_walking` …) resolved through SPEEDTYPE_CATALOG; walking when unset. */
function preferredSpeedVariable(caster: CachedCaster): string {
    const prefId = speedTypeId(ec(caster).preferred_speed_type) || speedTypeId(ftLayerValue(caster, 'default_preferred_speed_type'));
    if (prefId) {
        for (const s of SPEEDS) {
            const cat = window.SPEEDTYPE_CATALOG?.[s.v];
            if (cat && String(cat.item_id ?? '') === prefId) return s.v;
        }
    }
    return 'speed_walking';
}

/** LightCycleMode::DEFAULT_UUID (additions.php `default_light_cycle`). build_campaign_arr normalizes
 *  the column to this whenever it is missing/NULL/unknown, so it is the "nothing special going on"
 *  value: the overlay stays quiet for it and only names a cycle the GM has actually bent
 *  (Long Days · Long Nights · Endless Darkness/Dim/Bright). Owner 2026-09-03. */
const HALO_DEFAULT_LIGHT_CYCLE_ID = '64377EFA-FE12-9AFE-E20A-4189E2DF3EDF';

const HALO_R = 26;                       // ring radius in the 64×64 viewBox

/** Text the halo's hover overlay mirrors from the canonical readouts (one source of truth): the
 *  campaign clock chips in #ft_campaign_time_panel and the iframe minimap's campaign-hud card
 *  (encounter · location · zone · season). Same-origin iframe → direct DOM read.
 *
 *  ⛔ TEXT ONLY. This used to copy the card's icon markup with innerHTML/outerHTML and re-inject it
 *  into the halo; a tooltip-attribute strip was the only filter, which left event handlers, style,
 *  URLs and duplicate SVG ids intact (Codex review 2026-08-29). Same-origin does not make foreign
 *  markup safe. Labels come across as TEXT and every icon is re-rendered here from its own id. */
function haloOverlayContext(): { date: string; time: string; clock: { hh: string; mm: string; ampm: string; minutes: number } | null; fullDate: string; encounter: string; location: string; zone: string; seasonLabel: string; havenLabel: string; timeOfDay: string } {
    const txt = (root: ParentNode | null | undefined, sel: string) => (root?.querySelector<HTMLElement>(sel)?.textContent || '').trim();
    const doc = document;
    let frameDoc: Document | null = null;
    try {
        const frame = doc.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
        frameDoc = frame?.contentDocument ?? null;
    } catch { frameDoc = null; }
    const card = frameDoc?.querySelector<HTMLElement>('[data-ft3d-campaign-hud]') ?? null;
    const minimapHost = frameDoc?.querySelector<HTMLElement>('[data-ft3d-minimap]') ?? null;
    // The clock chip is the player's own readout — it already honours the 12h/24h setting. Parse it,
    // DROP the seconds (they would otherwise churn the repaint key every tick), keep the meridiem.
    const rawTime = txt(doc, '[data-ft-campaign-time-value="time"]');
    const m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp])?/.exec(rawTime);
    let clock: { hh: string; mm: string; ampm: string; minutes: number } | null = null;
    if (m) {
        const h12 = Number(m[1]);
        const ampm = (m[3] || '').toLowerCase();
        const h24 = ampm === 'p' && h12 < 12 ? h12 + 12 : (ampm === 'a' && h12 === 12 ? 0 : h12);
        clock = { hh: String(m[1]), mm: m[2] || '00', ampm, minutes: (h24 * 60) + Number(m[2]) };
    }
    // Label text only — the accessible name the card gave its season / camp-return icons.
    const labelOf = (root: ParentNode | null, sel: string) => {
        const n = root?.querySelector<HTMLElement>(sel) ?? null;
        return n ? (n.getAttribute('title') || n.getAttribute('aria-label') || n.getAttribute('alt') || '').trim() : '';
    };
    const havenNode = (() => {
        const status = minimapHost?.querySelector<HTMLElement>('[data-ft3d-hud-status]') ?? null;
        const nodes = status ? Array.from(status.querySelectorAll<HTMLElement>('img, svg, .ft-icon-map')) : [];
        return nodes.find(n => /haven|camp/i.test(`${n.getAttribute('alt') || ''} ${n.getAttribute('title') || ''} ${n.getAttribute('aria-label') || ''} ${n.parentElement?.getAttribute('title') || ''}`)) ?? null;
    })();
    return {
        date: txt(doc, '[data-ft-campaign-time-value="date"]'),
        time: clock ? `${clock.hh}:${clock.mm}${clock.ampm ? ` ${clock.ampm.toUpperCase()}M` : ''}` : rawTime.replace(/:(\d{2})(\s|$)/, '$2'),
        clock,
        havenLabel: havenNode
            ? (havenNode.closest<HTMLElement>('.ft-icon-map')?.getAttribute('title')
                || havenNode.getAttribute('title') || havenNode.getAttribute('aria-label') || havenNode.getAttribute('alt') || '').trim()
            : '',
        seasonLabel: labelOf(card, '[data-ft3d-hud-season] [title], [data-ft3d-hud-season] [alt], [data-ft3d-hud-season] [aria-label]'),
        fullDate: txt(doc, '[data-ft-campaign-time-value="full-date"]'),
        encounter: txt(card, '[data-ft3d-hud-encounter]'),
        location: txt(card, '[data-ft3d-hud-location]'),
        zone: txt(card, '[data-ft3d-hud-zone]'),
        // ⛔ SECONDS STRIPPED. The minimap card's time-of-day is a live clock ("2:16:54 PM"), and this
        // whole context object feeds turnHaloKey() — carrying seconds re-keyed the halo EVERY SECOND,
        // so the disc was torn down and rebuilt under the pointer and a click on the movement chip
        // never completed (mousedown and mouseup landed on different elements). Verified 2026-08-29:
        // the handler and the delegation were both fine; only the churn was wrong.
        timeOfDay: txt(card, '[data-ft3d-hud-time-of-day]').replace(/(\d{1,2}:\d{2}):\d{2}/, '$1'),
    };
}
/** The sky art inside the disc: a horizon, and the sun (day) or moon (night) travelling LEFT → RIGHT
 *  across it by the campaign clock. Day = 06:00–18:00, night = the other half; the body rises at the
 *  left edge, peaks at the top, sets at the right. */
/** Static weather art layered over the sky (owner 2026-08-28: at-a-glance, no animation). Keyed by
 *  the weather type's variable_name (shared/env-types WEATHER_TYPE_LIST). Coordinates are the sky's
 *  64×64 viewBox; the horizon is y≈42. */
function haloWeatherArt(weather: string): string {
    const cloud = (x: number, y: number, s = 1, cls = '') => `<g class="ft-turn-halo__cloud ${cls}" transform="translate(${x} ${y}) scale(${s})"><ellipse cx="0" cy="0" rx="7" ry="3.2"/><circle cx="-3" cy="-1.6" r="2.8"/><circle cx="1.5" cy="-2.6" r="3.4"/><circle cx="5" cy="-1" r="2.4"/></g>`;
    const drops = (cls: string, n: number, len: number, seed: number) => {
        let s = `<g class="ft-turn-halo__precip ${cls}">`;
        for (let i = 0; i < n; i++) {
            const x = 6 + ((i * 9.7 + seed) % 52);
            const y = 18 + ((i * 6.3 + seed * 2) % 22);
            s += `<line x1="${x.toFixed(1)}" y1="${y.toFixed(1)}" x2="${(x - len * .35).toFixed(1)}" y2="${(y + len).toFixed(1)}"/>`;
        }
        return `${s}</g>`;
    };
    const flakes = (cls: string, n: number, r: number, seed: number) => {
        let s = `<g class="ft-turn-halo__precip ${cls}">`;
        for (let i = 0; i < n; i++) {
            const x = 5 + ((i * 11.3 + seed) % 54);
            const y = 12 + ((i * 7.1 + seed * 3) % 28);
            s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r}"/>`;
        }
        return `${s}</g>`;
    };
    const fog = (cls: string) => `<g class="ft-turn-halo__fog ${cls}"><rect x="0" y="26" width="64" height="5" rx="2.5"/><rect x="0" y="33" width="64" height="4" rx="2"/><rect x="0" y="39" width="64" height="4" rx="2"/></g>`;
    switch (weather) {
        case 'partly_cloudy': return cloud(18, 24, .9) + cloud(46, 30, .7);
        case 'cloudy': return cloud(14, 22, 1) + cloud(34, 18, 1.1, 'is-dim') + cloud(50, 28, .9) + cloud(26, 32, .8, 'is-dim');
        case 'raining': return cloud(20, 18, 1.1, 'is-dim') + cloud(44, 16, 1, 'is-dim') + drops('is-rain', 7, 4, 3);
        case 'heavy_rain': return cloud(18, 16, 1.2, 'is-dark') + cloud(42, 14, 1.2, 'is-dark') + drops('is-rain', 12, 6, 5);
        case 'thunderstorm': return cloud(20, 16, 1.2, 'is-dark') + cloud(44, 14, 1.2, 'is-dark') + drops('is-rain', 8, 5, 7) + `<path class="ft-turn-halo__bolt" d="M33 20 L28 30 L32 30 L29 40 L37 27 L33 27 Z"/>`;
        case 'snowing': return cloud(20, 18, 1, 'is-dim') + cloud(44, 16, .9, 'is-dim') + flakes('is-snow', 8, 1.1, 2);
        case 'heavy_snow': return cloud(18, 16, 1.2, 'is-dim') + cloud(42, 14, 1.2, 'is-dim') + flakes('is-snow', 14, 1.3, 4);
        case 'sleet': return cloud(20, 18, 1, 'is-dim') + cloud(44, 16, .9, 'is-dim') + drops('is-rain', 5, 4, 2) + flakes('is-snow', 5, 1, 6);
        case 'hail': return cloud(20, 17, 1.1, 'is-dark') + cloud(44, 15, 1, 'is-dark') + flakes('is-hail', 9, 1.4, 3);
        case 'hazy': return fog('is-haze');
        case 'dust_storm': return fog('is-dust') + flakes('is-dust', 10, .9, 5);
        case 'ashfall': return cloud(24, 16, 1.2, 'is-ash') + cloud(46, 20, 1, 'is-ash') + flakes('is-ash', 10, 1, 8);
        default: return '';   // clear
    }
}

/** The "land" under the sky, shaped by the encounter's environment (the xz-bg manifest's top-level
 *  env keys: forest · hill · mountain · grassland · desert · arctic · swamp · coast · urban ·
 *  underground · planar · outer_space · underwater). Unknown/empty → gentle plain. */
function haloGroundArt(env: string): string {
    const plain = `<path class="ft-turn-halo__ground" d="M0 42 Q16 38 32 42 T64 42 L64 64 L0 64 Z"/>`;
    switch (env) {
        case 'forest':
            return `<path class="ft-turn-halo__ground" d="M0 44 Q16 41 32 44 T64 44 L64 64 L0 64 Z"/>`
                + `<g class="ft-turn-halo__trees"><path d="M8 44 L11 34 L14 44 Z"/><path d="M13 44 L17 30 L21 44 Z"/><path d="M24 44 L27 36 L30 44 Z"/><path d="M40 44 L44 31 L48 44 Z"/><path d="M50 44 L53 36 L56 44 Z"/><path d="M56 44 L59 38 L62 44 Z"/></g>`;
        case 'hill':
            return `<path class="ft-turn-halo__ground is-far" d="M0 40 Q14 30 30 38 Q46 46 64 36 L64 64 L0 64 Z"/><path class="ft-turn-halo__ground" d="M0 48 Q20 40 40 47 Q54 51 64 46 L64 64 L0 64 Z"/>`;
        case 'mountain':
            return `<path class="ft-turn-halo__mountain is-far" d="M-2 46 L12 24 L22 36 L34 18 L46 34 L56 26 L66 44 L66 64 L-2 64 Z"/><path class="ft-turn-halo__snowcap" d="M34 18 L30 25 L38 25 Z M12 24 L9 29 L15 29 Z"/><path class="ft-turn-halo__ground" d="M0 50 Q20 44 40 49 T64 48 L64 64 L0 64 Z"/>`;
        case 'grassland':
            return `<path class="ft-turn-halo__ground is-grass" d="M0 43 Q20 41 40 43 T64 42 L64 64 L0 64 Z"/>`;
        case 'desert':
            return `<path class="ft-turn-halo__ground is-sand is-far" d="M0 44 Q18 36 34 42 T64 40 L64 64 L0 64 Z"/><path class="ft-turn-halo__ground is-sand" d="M0 50 Q22 44 40 49 T64 47 L64 64 L0 64 Z"/>`;
        case 'arctic':
            return `<path class="ft-turn-halo__ground is-snow is-far" d="M0 42 Q16 38 32 42 T64 40 L64 64 L0 64 Z"/><path class="ft-turn-halo__ground is-snow" d="M0 49 Q20 45 40 49 T64 47 L64 64 L0 64 Z"/>`;
        case 'swamp':
            return `<path class="ft-turn-halo__ground is-marsh" d="M0 43 Q16 41 32 43 T64 42 L64 64 L0 64 Z"/><g class="ft-turn-halo__reeds"><path d="M12 44 L12 36 M15 44 L16 35 M44 44 L44 37 M48 44 L49 35 M52 44 L52 38"/></g><rect class="ft-turn-halo__water" x="0" y="50" width="64" height="14"/>`;
        case 'coast':
            return `<path class="ft-turn-halo__ground is-sand" d="M0 43 Q16 40 32 43 T64 42 L64 64 L0 64 Z"/><path class="ft-turn-halo__water" d="M0 49 Q10 47 20 49 T40 49 T60 49 T80 49 L80 64 L0 64 Z"/>`;
        case 'urban':
            return `<path class="ft-turn-halo__ground is-stone" d="M0 44 L64 44 L64 64 L0 64 Z"/><g class="ft-turn-halo__buildings"><rect x="6" y="30" width="8" height="14"/><rect x="16" y="24" width="10" height="20"/><rect x="29" y="33" width="7" height="11"/><rect x="39" y="26" width="9" height="18"/><rect x="51" y="31" width="8" height="13"/></g>`;
        case 'underground':
            return `<rect class="ft-turn-halo__rock" x="0" y="0" width="64" height="64"/><path class="ft-turn-halo__cavern" d="M6 64 Q8 34 22 30 Q32 18 44 30 Q58 34 60 64 Z"/><path class="ft-turn-halo__ground is-stone" d="M0 48 L64 48 L64 64 L0 64 Z"/>`;
        case 'underwater':
            return `<rect class="ft-turn-halo__water is-deep" x="0" y="0" width="64" height="64"/><path class="ft-turn-halo__ground is-sand" d="M0 50 Q16 46 32 50 T64 49 L64 64 L0 64 Z"/><g class="ft-turn-halo__reeds is-kelp"><path d="M14 50 Q12 42 15 36 M46 50 Q48 41 45 34"/></g>`;
        case 'outer_space':
            return `<rect class="ft-turn-halo__void" x="0" y="0" width="64" height="64"/><g class="ft-turn-halo__stars"><circle cx="10" cy="20" r=".9"/><circle cx="24" cy="50" r=".8"/><circle cx="40" cy="12" r=".7"/><circle cx="54" cy="40" r=".9"/><circle cx="32" cy="34" r=".6"/><circle cx="18" cy="58" r=".7"/></g>`;
        case 'planar':
            return `<path class="ft-turn-halo__ground is-planar" d="M0 44 Q16 36 32 44 T64 42 L64 64 L0 64 Z"/>`;
        default:
            return plain;
    }
}

function haloSkyArt(minutes: number, weather = '', env = ''): { svg: string; day: boolean } {
    const m = ((minutes % 1440) + 1440) % 1440;
    const day = m >= 360 && m < 1080;
    const t = day ? (m - 360) / 720 : ((m + 360) % 1440) / 720;   // 0 = rise, 1 = set
    const x = 8 + 48 * t;
    const y = 42 - 26 * Math.sin(Math.PI * t);
    const body = day
        ? `<circle class="ft-turn-halo__sun" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6"/>`
        : `<g class="ft-turn-halo__moon" transform="translate(${x.toFixed(1)} ${y.toFixed(1)})"><circle r="6"/><circle class="ft-turn-halo__moon-bite" cx="3" cy="-2" r="5"/></g>`;
    const stars = day ? '' : `<g class="ft-turn-halo__stars"><circle cx="14" cy="14" r=".9"/><circle cx="46" cy="10" r=".8"/><circle cx="30" cy="8" r=".7"/><circle cx="52" cy="24" r=".7"/><circle cx="20" cy="26" r=".6"/></g>`;
    // No sky/sun/weather where there is no sky: caves, the void, under the sea.
    const skyless = env === 'underground' || env === 'outer_space' || env === 'underwater';
    return {
        day,
        svg: `<svg class="ft-turn-halo__sky ft-turn-halo__sky--${escapeHtml(weather || 'clear')} ft-turn-halo__sky--env-${escapeHtml(env || 'plain')}" viewBox="0 0 64 64" aria-hidden="true">`
            + `<defs><clipPath id="ftHaloSkyClip"><circle cx="32" cy="32" r="30"/></clipPath></defs>`
            + `<g clip-path="url(#ftHaloSkyClip)">`
            + `<rect class="ft-turn-halo__skyfill" x="0" y="0" width="64" height="64"/>`
            + (skyless ? '' : stars + body + haloWeatherArt(weather))
            + haloGroundArt(env)
            + `</g></svg>`,
    };
}

/** Combat deliberately uses a purpose-built hourglass instead of the campaign sky/environment art.
 *  It is inline SVG so the End Turn affordance is immediate even while the icon CDN is still mounting. */
function haloHourglassArt(): string {
    return `<svg class="ft-turn-halo__hourglass-art" viewBox="0 0 64 64" aria-hidden="true">`
        + `<path class="ft-turn-halo__hourglass-rim" d="M17 10h30v6H17zM17 48h30v6H17z"/>`
        + `<path class="ft-turn-halo__hourglass-frame" d="M20 16h5c0 8 2.7 11.4 7 15.1C27.7 35 25 38.5 25 48h-5c0-9.8 2.4-14.4 7.1-17C22.4 27.5 20 23 20 16zm24 0h-5c0 8-2.7 11.4-7 15.1C36.3 35 39 38.5 39 48h5c0-9.8-2.4-14.4-7.1-17C41.6 27.5 44 23 44 16z"/>`
        + `<path class="ft-turn-halo__hourglass-glass" d="M25 16h14c0 7.3-2.4 10.1-7 13.9-4.6-3.8-7-6.6-7-13.9zm7 16.4c4.6 3.8 7 7.2 7 15.6H25c0-8.4 2.4-11.8 7-15.6z"/>`
        + `<path class="ft-turn-halo__hourglass-sand" d="M27 19h10c-.5 3.7-2.1 6-5 8.6-2.9-2.6-4.5-4.9-5-8.6zm5 17.1c3.1 2.8 4.4 5.4 4.8 9.2h-9.6c.4-3.8 1.7-6.4 4.8-9.2z"/>`
        + `<path class="ft-turn-halo__hourglass-stream" d="M31.2 28h1.6v10h-1.6z"/>`
        + `</svg>`;
}

/**
 * BG3-style End-Turn halo for the FT3D campaign action bar (owner 2026-08-27/28). Sits just RIGHT of
 * the dock. Out of combat, the core is sky art (sun/moon over the horizon by the campaign clock) and
 * hovering it opens the full calendar + encounter/location overlay. Combat keeps that context available:
 * the in-combat mark sits above a purpose-built End Turn hourglass or the current actor's focusable portrait;
 * another actor's turn opens the panel on hover, while your End Turn state opens it only from that mark;
 * the chip under it is ALWAYS the preferred-movement button (opens the Movement fly-up).
 *  IN COMBAT  — the ring is the banked movement (movement_pct, clamped to one AP); on your own turn the
 *               core becomes the End Turn button; the whole thing pulses once AP and the reaction
 *               are both spent. The preferred-movement chip remains, without the retired feet badge.
 *  OUT OF COMBAT — the ring is the campaign clock (noon at the top).
 * '' when the caster has no encounter binding AND no campaign clock (nothing to show).
 */
/** Turn state the hands-cluster PORTRAIT presents in combat (owner 2026-09-15): the End Turn strip, the
 *  movement rim around the portrait frame, and the out-of-AP End Turn prompt. null out of combat.
 *  The disc by the minimap is context only; turn CONTROL lives with the unit you are driving. */
export type PortraitTurnState = {
    myTurn: boolean;
    canEndTurn: boolean;
    /** No action points left on this caster's own turn. Reactions deliberately do not count. */
    outOfAp: boolean;
    fillPct: number;
    bankedPct: number;
    aps: number;
    baseSpeed: number;
    remainingFeet: number;
    speedVariable: string;
    /** '' for walking — the rim only names the mode when it is not the default. */
    speedLabel: string;
    entityId: string;
    encounterId: string;
    roundNo: number;
};

export function portraitTurnState(caster: CachedCaster): PortraitTurnState | null {
    const enc = haloEncounter(caster);
    if (num(encField(enc, 'in_combat')) !== 1) return null;
    // The campaign action bar treats owner OR GM permission as control (action-bar.ts `canControl`);
    // End Turn keeps that same contract. The server remains the final turn/permission gate on click.
    const canEndTurn = (typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster))
        || (typeof ftHasCampaignPermission === 'function' && ftHasCampaignPermission(caster));
    const entityId = String(scalarOf(ec(caster).encounter_creature_id) ?? '').trim().toUpperCase();
    const myTurn = entityId !== ''
        && String(encField(enc, 'current_encounter_creature_id_turn') ?? '').trim().toUpperCase() === entityId;
    const aps = num(ec(caster).aps);
    const speedVariable = preferredSpeedVariable(caster);
    const baseSpeed = Math.max(0, speedTotal(caster, speedVariable));
    const fillPct = haloMovementPct(caster, speedVariable);
    const label = speedVariable === 'teleport' ? 'teleport' : speedVariable.slice(6);
    return {
        myTurn,
        canEndTurn,
        outOfAp: myTurn && aps <= 0,
        fillPct,
        bankedPct: casterMovementPct(caster),
        aps,
        baseSpeed,
        remainingFeet: Math.max(0, Math.round(fillPct * baseSpeed)),
        speedVariable,
        speedLabel: speedVariable === 'speed_walking' ? '' : label,
        entityId,
        encounterId: String(scalarOf(ec(caster).encounter_id) ?? '').trim().toUpperCase(),
        roundNo: num(encField(enc, 'current_round')),
    };
}

export function renderTurnHalo(caster: CachedCaster, casterId: string): string {
    const haloEnc = haloEncounter(caster);
    const combat = num(encField(haloEnc, 'in_combat')) === 1;
    // Clock truth comes from the campaign cache. The DOM readout contributes FORMAT only (12h/24h):
    // on a campaign WS push it can briefly carry the previous value, which used to leave this halo
    // exactly one event behind even though campaignCache already held the backend truth.
    const ctx = haloOverlayContext();
    const camp = resolveCampaign(caster);
    const truthClock = campaignClockHm(camp);
    const cacheHasClock = truthClock !== null;
    const hasClock = ctx.clock !== null || cacheHasClock;
    const cacheHour = truthClock?.hour ?? 0;
    const cacheMinute = truthClock?.minute ?? 0;
    const minutes = cacheHasClock ? (cacheHour * 60) + cacheMinute : (ctx.clock?.minutes ?? 720);
    const useTwelveHour = !!ctx.clock?.ampm;
    const hh = cacheHasClock
        ? (useTwelveHour ? String((cacheHour % 12) || 12) : String(cacheHour).padStart(2, '0'))
        : (ctx.clock?.hh ?? '12');
    const mm = cacheHasClock ? String(cacheMinute).padStart(2, '0') : (ctx.clock?.mm ?? '00');
    const ampm = cacheHasClock && useTwelveHour ? (cacheHour >= 12 ? 'p' : 'a') : (ctx.clock?.ampm ?? '');
    const clockLabel = `${hh}:${mm}${ampm ? `<small class="ft-turn-halo__ampm">${ampm}</small>` : ''}`;
    if (!combat && !hasClock) return '';

    // Ring: a plain gradient rim. Movement no longer rides this disc (owner 2026-09-15) — it drains
    // around the hands-cluster portrait instead (portraitTurnState).
    const ring = `<svg class="ft-turn-halo__ring" viewBox="0 0 64 64" aria-hidden="true">`
        + `<defs>`
        + `<linearGradient id="ftHaloRimGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#5bcdca" stop-opacity=".55"/><stop offset=".55" stop-color="#1c2d33" stop-opacity=".9"/><stop offset="1" stop-color="#5bcdca" stop-opacity=".35"/></linearGradient>`
        + `</defs>`
        + `<circle class="ft-turn-halo__track" cx="32" cy="32" r="${HALO_R}"/>`
        + `</svg>`;
    // Weather (encounter row; string uuid or a reshaped {items:{item_id}} shell) drives the sky art
    // overlay AND its overlay row; the icon comes off the weather item's own UUID (shared catalog).
    const weatherRaw = encField(resolveEncounter(caster), 'encounter_weather');
    const weatherId = speedTypeId(weatherRaw);
    const weatherRow = WEATHER_TYPE_LIST.find(r => r.item_id.toUpperCase() === weatherId.toUpperCase());
    // Environment (biome) → shapes the land in the disc and backs the overlay header with the same
    // CDN backdrop the XZ parallax uses (images/xz-bg/<env>[/<variant>]/far[_night].webp).
    const envInfo = haloEnvironment(caster);
    const sky = haloSkyArt(minutes, weatherRow?.variable_name ?? '', envInfo.key);
    const roundNo = num(encField(haloEnc, 'current_round'));
    const turnActor = combat ? currentTurnActor(caster) : null;

    // Hover overlay — header (where we are), then groups: time + dates | weather + season | the
    // informational tail (camp return, combat state). Mirrors the canonical readouts.
    const row = (icon: string, text: string, muted = false) => `<div class="ft-turn-halo__ov-row${muted ? ' ft-turn-halo__ov-muted' : ''}"><span>${icon}</span><span>${text}</span></div>`;
    const headerArt = envInfo.art ? ` style="background-image:url('${escapeHtml(envInfo.art)}')"` : '';
    // Biome as its ICON (the environment item's own icon via the shared UUID catalog); the name only
    // as the tooltip, or as a text chip when no icon exists for it.
    const envIcon = envInfo.uuid ? iconMap(envInfo.uuid, 20, 20) : '';
    const envChip = envIcon
        ? `<span class="ft-turn-halo__ov-env ft-turn-halo__ov-env--icon" title="${escapeHtml(envInfo.name)}">${envIcon}</span>`
        : (envInfo.name ? `<span class="ft-turn-halo__ov-env">${escapeHtml(envInfo.name)}</span>` : '');
    // Place breadcrumb: the location's MASTER PARENT ahead of the location itself ("Barrowmoor ›
    // The Halls of the Blind"), so the header says where in the world this is. The parent is only a
    // FALLBACK in my_campaign.php's hud payload, so read it off the encounter row directly; skipped
    // when it is empty or the same string as the location (owner 2026-08-28).
    // Upstream chain from my_campaign.php (build_location_ancestor_nav_map_rows → names, root-first,
    // current location last) when it was built for THIS encounter; otherwise the 1-hop parent off the
    // encounter row, so a party that has moved on still gets context rather than a stale world.
    const chainRaw = (window as unknown as { ft3dLocationChain?: { encounterId?: string; names?: unknown } }).ft3dLocationChain;
    const liveEncId = String(scalarOf(encField(resolveEncounter(caster), 'encounter_id')) ?? '').trim().toLowerCase();
    const chainNames = (Array.isArray(chainRaw?.names) && String(chainRaw?.encounterId ?? '').trim().toLowerCase() === liveEncId && liveEncId !== '')
        ? (chainRaw!.names as unknown[]).map(n => String(n ?? '').trim()).filter(Boolean)
        : [];
    const locParent = String(scalarOf(encField(resolveEncounter(caster), 'encounter_location_parent_name')) ?? '').trim();
    const showParent = locParent !== '' && locParent.toLowerCase() !== ctx.location.trim().toLowerCase();
    // Nested place chain, one rung per LINE with a down-arrow gutter (view_map.php's nesting read):
    //   Barrowmoor
    //    ↳ The Halls of the Blind (HotB 100.0)
    // The encounter itself is the <strong> above; each rung below is one step further IN.
    const rung = (text: string, depth: number) => `<span class="ft-turn-halo__ov-rung" style="--rung:${depth}">${depth ? '<span class="ft-turn-halo__ov-sep" aria-hidden="true">↳</span>' : ''}${text}</span>`;
    const rungs: string[] = [];
    if (chainNames.length) {
        chainNames.forEach((name, i) => {
            const here = i === chainNames.length - 1;
            const text = here
                ? `${escapeHtml(name)}${ctx.zone ? ` ${escapeHtml(ctx.zone)}` : ''}`
                : `<span class="ft-turn-halo__ov-parent">${escapeHtml(name)}</span>`;
            rungs.push(rung(text, i));
        });
    } else {
        if (showParent) rungs.push(rung(`<span class="ft-turn-halo__ov-parent">${escapeHtml(locParent)}</span>`, 0));
        if (ctx.location) rungs.push(rung(`${escapeHtml(ctx.location)}${ctx.zone ? ` ${escapeHtml(ctx.zone)}` : ''}`, showParent ? 1 : 0));
    }
    const placeLine = rungs.join('');
    // Header is two bands (owner 2026-08-29): the CAMPAIGN name in a gradient title bar, then the
    // place band under it — encounter + upstream location chain, with the biome badge on that row.
    const campaignName = String(scalarOf(campField(camp, 'campaign_name')) ?? '').trim();
    const campaignBand = campaignName ? `<div class="ft-turn-halo__ov-campaign">${escapeHtml(campaignName)}</div>` : '';
    const placeBand = ctx.encounter
        ? `<div class="ft-turn-halo__ov-place${envInfo.art ? ' has-art' : ''}"${headerArt}><div class="ft-turn-halo__ov-place-text"><strong>${escapeHtml(ctx.encounter)}</strong>${placeLine}</div>${envChip}</div>`
        : '';
    const header = campaignBand + placeBand;
    const truthTime = `${hh}:${mm}${ampm ? ` ${ampm.toUpperCase()}M` : ''}`;
    const g1: string[] = [row(iconMap('calendar_time', 16, 16), escapeHtml(cacheHasClock ? truthTime : (ctx.time || truthTime)))];
    if (ctx.date) g1.push(row(iconMap('calendar_date', 16, 16), escapeHtml(ctx.date)));
    if (ctx.fullDate) g1.push(row(iconMap('calendar', 16, 16), escapeHtml(ctx.fullDate)));
    const g2: string[] = [];
    if (weatherRow) g2.push(row(iconMap(weatherRow.item_id, 16, 16) || iconMap('encounter_weather', 16, 16), escapeHtml(weatherRow.item_name)));
    // Season icon rendered from the campaign's own month_season id — never mirrored markup.
    const seasonId = String(scalarOf(campField(camp, 'month_season')) ?? '').trim();
    if (seasonId) g2.push(row(iconMap(seasonId, 16, 16), escapeHtml(ctx.seasonLabel || 'Season')));
    // Lighting (owner 2026-09-03): the three inputs FT3D actually lights the scene from, in the order
    // they resolve — the sun state the campaign clock currently classifies into, the light in force
    // (and whether that is the GM's per-encounter override or the campaign cycle), and the cycle mode
    // driving both. Every id is re-rendered through iconMap from our own catalog; the {items:{…}}
    // shells build_campaign_arr/build_encounter_arr write carry the display name with the id.
    // Their OWN group (owner 2026-09-03), so the `.ft-turn-halo__ov-group + …` rule rules them off
    // from the weather/season pair instead of stacking five undifferentiated rows.
    const gLight: string[] = [];
    // A GM light override on the encounter DECOUPLES the scene from the clock: resolveSceneLighting()
    // derives its sun look from the LIGHT under an override (bright→day, dim→sunset, pitch-black and
    // magical-darkness→night) and forces cloud cover to 0, so a forced Bright renders as a clear noon
    // at 02:00. Staging audit 2026-09-03: nearly every live encounter carries an override, so this is
    // the COMMON case — the sun-state row is muted and marked "clock only" there, because it is then
    // the campaign's clock talking, not the light the player is standing in.
    const encLightId = speedTypeId(encField(haloEnc, 'encounter_light'));
    const overridden = lightTypeName(encLightId) !== '';
    const sunStateShell = campField(camp, 'campaign_sun_state');
    const sunStateId = speedTypeId(sunStateShell);
    if (sunStateId) {
        // The catalog names carry a "Sun State " prefix ("Sun State Day"); the readout wants the state.
        const sunLabel = shellName(sunStateShell).replace(/^sun\s*state\s+/i, '') || 'Sun state';
        gLight.push(row(
            iconMap(sunStateId, 16, 16) || iconMap('sun_state', 16, 16),
            escapeHtml(overridden ? `${sunLabel} · clock only` : sunLabel),
            overridden,
        ));
    }
    // effectiveEncounterLightId() is the SAME nullable-override → campaign-light fallback the 2D map,
    // vision and PHP's ft_effective_encounter_light() resolve, so this row can never disagree with the
    // light the player is actually looking at.
    const lightId = effectiveEncounterLightId(haloEnc, camp) ?? '';
    if (lightId) {
        const lightName = lightTypeName(lightId)
            || shellName(overridden ? encField(haloEnc, 'encounter_light') : campField(camp, 'campaign_light'))
            || 'Light';
        gLight.push(row(
            iconMap(lightId, 16, 16) || iconMap('encounter_light', 16, 16),
            escapeHtml(`${lightName} · ${overridden ? 'encounter override' : 'campaign cycle'}`),
        ));
    }
    // The cycle mode behind the pair above — shown ONLY when it is not the default (owner 2026-09-03):
    // "Default Light Cycle" on every card is noise, while Long Days / Long Nights / an Endless mode
    // changes how to read the two rows above it and has to be visible. LightCycleMode has no per-mode
    // icon minted, so the one light_cycle_mode glyph stands for all of them.
    const lightCycleShell = campField(camp, 'campaign_light_cycle_mode');
    const lightCycleId = speedTypeId(lightCycleShell).toUpperCase();
    const lightCycleName = shellName(lightCycleShell);
    if (lightCycleName && lightCycleId !== HALO_DEFAULT_LIGHT_CYCLE_ID) {
        gLight.push(row(iconMap('light_cycle_mode', 16, 16), escapeHtml(lightCycleName), true));
    }
    const g3: string[] = [];
    // Camp/haven return: state derived here (combat hides it; the campaign flag disables it), icon
    // rendered from our own catalog — never mirrored markup.
    if (!combat) {
        const havenOff = num(campField(camp, 'campaign_disable_player_haven_return')) === 1;
        g3.push(row(
            iconMap(havenOff ? 'haven_return_disabled' : 'haven_return_enabled', 16, 16),
            escapeHtml(ctx.havenLabel || (havenOff ? 'Return to camp or haven unavailable' : 'Return to camp or haven available')),
            true,
        ));
    }
    g3.push(row(iconMap(combat ? 'in_combat' : 'out_of_combat', 16, 16), combat ? `In combat${roundNo ? ` · round ${roundNo}` : ''}` : 'Out of combat', true));
    if (combat && turnActor) {
        g3.push(row(iconMap('go_to_creature_turn', 16, 16), escapeHtml(`Current turn: ${turnActor.name}`)));
    }
    const groups = [g1, g2, gLight, g3].filter(g => g.length).map(g => `<div class="ft-turn-halo__ov-group">${g.join('')}</div>`).join('');
    const overlayId = `ft_turn_halo_info_${casterId.replace(/[^A-Za-z0-9_-]/g, '')}`;
    const overlay = `<div id="${overlayId}" class="ft-turn-halo__overlay" role="tooltip">${header}<div class="ft-turn-halo__ov-body">${groups}</div></div>`;

    // ONE disc in both modes (owner 2026-09-15): the campaign sky + clock, parked beside the minimap.
    // End Turn and the movement rim moved to the hands-cluster portrait (portraitTurnState), and the
    // preferred-movement chip is gone — the Movement category is one click away on the bar itself.
    // In combat the disc keeps the state mark and, while somebody else owns the turn, their face as
    // a camera-focus badge, so "who is up" stays readable without the hover overlay.
    const casterEcId = String(scalarOf(ec(caster).encounter_creature_id) ?? '').trim().toUpperCase();
    const combatInfoLabel = `In combat${roundNo ? `, round ${roundNo}` : ''}`;
    const combatMark = combat
        ? `<span class="ft-turn-halo__combat-info" role="img" aria-label="${escapeHtml(combatInfoLabel)}">${iconMap('in_combat', 18, 18, false)}</span>`
        : '';
    const turnBadge = (combat && turnActor && turnActor.id.trim().toUpperCase() !== casterEcId)
        ? (() => {
            const name = escapeHtml(turnActor.name);
            const fallback = `<span class="ft-turn-halo__turn-badge-initials" aria-hidden="true">${escapeHtml(actorInitials(turnActor.name))}</span>`;
            const picture = turnActor.pictureUrl
                ? `<img src="${escapeHtml(turnActor.pictureUrl)}" alt="" draggable="false" onerror="this.remove()">`
                : '';
            return `<button type="button" class="ft-turn-halo__turn-badge" data-ft-ab-action="focus-turn-creature" data-ft-entity-id="${escapeHtml(turnActor.id)}" title="${name}'s turn — focus camera" aria-label="${name}'s turn — focus camera on them">${fallback}${picture}</button>`;
        })()
        : '';
    const cls = 'ft-turn-halo ft-turn-halo--daylight'
        + (combat ? ' ft-turn-halo--combat' : '')
        + (sky.day ? ' is-day' : ' is-night');
    return `<div class="${cls}" data-ft-turn-halo="${escapeHtml(casterId)}" tabindex="0">`
        + ring
        + `<span class="ft-turn-halo__core" aria-label="Campaign time ${hh}:${mm}${ampm ? ` ${ampm}m` : ''}">${sky.svg}<span class="ft-turn-halo__core-label"><span class="ft-turn-halo__clock">${clockLabel}</span></span></span>`
        + combatMark
        + turnBadge
        + overlay
        + `</div>`;
}

/** The hourglass glyph, shared with the action bar's out-of-AP End Turn prompt. */
export function turnHourglassArt(): string {
    return haloHourglassArt();
}

/** Stable repaint key for the halo — everything renderTurnHalo's output depends on. The action bar
 *  compares THIS (not the markup: iconMap output carries per-call ids, so byte-comparing HTML repainted
 *  the disc on every caster update and killed :hover / focus on it). */
export function turnHaloKey(caster: CachedCaster, casterId: string): string {
    const camp = resolveCampaign(caster);
    const enc = haloEncounter(caster);
    const combat = num(encField(enc, 'in_combat')) === 1;
    const env = haloEnvironment(caster);
    const turnActor = currentTurnActor(caster);
    // Common to both modes: identity, the disc's own state, and the sky/land art inputs.
    const base: unknown[] = [
        casterId,
        combat,
        String(encField(enc, 'current_encounter_creature_id_turn') ?? ''),
        String(scalarOf(ec(caster).encounter_creature_id) ?? ''),
        campaignClockHm(camp)?.hour ?? -1,
        campaignClockHm(camp)?.minute ?? -1,
        num(encField(enc, 'current_round')),
        turnActor?.id ?? '',
        turnActor?.name ?? '',
        turnActor?.pictureUrl ?? '',
        casterDisplayName(caster),
        casterPortraitUrl(caster),
        speedTypeId(encField(enc, 'encounter_weather')),
        // Every environment field the renderer reads, not just the art url: two envs with no art and
        // no name resolver would otherwise key identically while drawing different icons.
        env.key, env.name, env.uuid, env.art,
    ];
    // Both modes now render the context panel. Seconds remain stripped by haloOverlayContext(), so
    // including its visible inputs does not churn the halo under hover/focus every second.
    const chain = (window as unknown as { ft3dLocationChain?: { encounterId?: string; names?: unknown } }).ft3dLocationChain;
    return JSON.stringify([
        ...base,
        String(scalarOf(campField(camp, 'campaign_name')) ?? ''),
        String(scalarOf(encField(enc, 'encounter_location_parent_name')) ?? ''),
        String(scalarOf(campField(camp, 'month_season')) ?? ''),
        // The overlay's lighting rows. Without these the halo keeps a stale light through a sunset,
        // a GM light override or a cycle-mode change — none of which touch any other key input.
        speedTypeId(campField(camp, 'campaign_sun_state')),
        speedTypeId(campField(camp, 'campaign_light')),
        speedTypeId(campField(camp, 'campaign_light_cycle_mode')),
        speedTypeId(encField(enc, 'encounter_light')),
        num(campField(camp, 'campaign_disable_player_haven_return')),
        // The rendered breadcrumb: an upstream RENAME leaves the location itself untouched, so
        // without these the chain would stay stale forever.
        String(chain?.encounterId ?? ''),
        Array.isArray(chain?.names) ? (chain!.names as unknown[]).map(n => String(n ?? '')).join('') : '',
        haloOverlayContext(),
    ]);
}

// These environments intentionally store their default backdrop in a named variant directory.
// Keep this aligned with FT3D_BG_MANIFEST's default aliases; importing that art-heavy manifest into
// the character-sheet bundle would pull the complete procedural backdrop kit into this UI module.
const HALO_DEFAULT_BACKDROP_VARIANT: Readonly<Record<string, string>> = {
    urban: 'castle',
    underground: 'cave',
    planar: 'fire',
    outer_space: 'starfield',
    underwater: 'reef',
};

/** The encounter's environment (biome): manifest key (`forest`, `desert`, …), display name, and the
 *  CDN backdrop the XZ parallax uses for it — day `far.webp`, night `far_night.webp`, under
 *  images/xz-bg/<env>[/<variant>]/. `getEnvironmentName` is template.js's uuid → name resolver. */
export function haloEnvironment(caster: CachedCaster): { key: string; name: string; art: string; uuid: string } {
    const enc = resolveEncounter(caster);
    const uuid = speedTypeId(encField(enc, 'encounter_environment')) || speedTypeId(encField(enc, 'location_environment'));
    const resolver = (window as unknown as { getEnvironmentName?: (id: string) => string }).getEnvironmentName;
    const name = uuid && typeof resolver === 'function' ? String(resolver(uuid) || '') : '';
    const key = name.trim().toLowerCase().replace(/[\s-]+/g, '_');
    // ⛔ Both path segments are DB text landing inside a CSS `url('…')`. HTML-escaping does NOT
    // protect that context (entities decode before the CSS parser runs), so a quote or a `../` in a
    // GM-authored variant could break out of the url() or walk the path. Slug-gate both; anything
    // else means no art (Codex review 2026-08-29).
    const SLUG = /^[a-z0-9_]+$/;
    const variantRaw = String(scalarOf(encField(enc, 'encounter_backdrop_variant')) ?? '').trim().toLowerCase();
    const variant = SLUG.test(variantRaw) ? variantRaw : '';
    const resolvedVariant = variant && variant !== '_default'
        ? variant
        : (HALO_DEFAULT_BACKDROP_VARIANT[key] ?? '');
    // Same CDN key the FT3D backdrop manifest uses (BG_ORIGIN) — assets never come from the site origin.
    const cdn = 'https://cdn.fatetide.com';
    if (!key || !SLUG.test(key)) return { key, name, art: '', uuid };
    const camp = resolveCampaign(caster);
    const truthClock = campaignClockHm(camp);
    const cacheMinutes = truthClock ? (truthClock.hour * 60) + truthClock.minute : null;
    const domClock = haloOverlayContext().clock;
    const clockMinutes = cacheMinutes ?? domClock?.minutes ?? null;
    const night = clockMinutes !== null ? (clockMinutes < 360 || clockMinutes >= 1080) : false;
    const skylessNight = key === 'underground' || key === 'outer_space' || key === 'underwater' || key === 'planar';
    const file = night && !skylessNight ? 'far_night.webp' : 'far.webp';
    const art = `${cdn}/images/xz-bg/${key}${resolvedVariant ? `/${resolvedVariant}` : ''}/${file}`;
    return { key, name, art, uuid };
}
